/**
 * Maps GET /api/v1/audiences/{id}/batches items to accordion progress UI.
 *
 * API shape:
 * {
 *   batchNo: string,
 *   entryCount: number,
 *   validEntryCount: number,
 *   requestId: number | null,
 *   completedOn: string | null,
 *   pendingItemCount: number | null,
 *   status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED"
 * }
 */

export const normalizeAudienceBatches = (batches) => {
    if (Array.isArray(batches)) {
        return [...batches];
    }
    if (batches?.content && Array.isArray(batches.content)) {
        return [...batches.content];
    }
    return [];
};

export const buildBatchMetaByBatchNo = (batches) => {
    const map = new Map();
    normalizeAudienceBatches(batches).forEach((batch) => {
        map.set(String(batch.batchNo ?? ""), batch);
    });
    return map;
};

const normalizeStatus = (status) => {
    if (status == null || status === "") {
        return null;
    }
    return String(status).toUpperCase().replace(/-/g, "_");
};

const getApiStatus = (batchMeta) => {
    if (!batchMeta) {
        return "NOT_STARTED";
    }

    if (batchMeta.completedOn) {
        return "COMPLETED";
    }

    const status = normalizeStatus(batchMeta.status);
    if (status === "NOT_STARTED" || status === "IN_PROGRESS" || status === "COMPLETED") {
        return status;
    }

    if (batchMeta.requestId != null) {
        return "IN_PROGRESS";
    }
    return "NOT_STARTED";
};

const getImportedCountFromRows = (batchRows = []) =>
    batchRows.filter((entry) => entry.imported).length;

const getCountsFromBatchMeta = (batchMeta, batchRows = []) => {
    const entryCount = Number(batchMeta.entryCount) || 0;
    const validEntryCount = Number(batchMeta.validEntryCount) || entryCount;
    const total = validEntryCount;
    const apiStatus = getApiStatus(batchMeta);
    const pendingItemCount = batchMeta.pendingItemCount;

    let importedCount = 0;

    if (apiStatus === "IN_PROGRESS") {
        if (pendingItemCount != null) {
            importedCount = Math.max(0, validEntryCount - Number(pendingItemCount));
        } else if (batchRows.length > 0) {
            importedCount = getImportedCountFromRows(batchRows);
        }
    } else if (apiStatus === "COMPLETED") {
        importedCount = validEntryCount;
    }

    const percent = total ? Math.round((importedCount / total) * 100) : 0;

    return {
        total,
        importedCount,
        percent,
        entryCount,
        validEntryCount,
        apiStatus,
    };
};

const getCountsFromRows = (batchRows = []) => {
    const total = batchRows.length;
    const importedCount = getImportedCountFromRows(batchRows);
    const percent = total ? Math.round((importedCount / total) * 100) : 0;
    return {
        total,
        importedCount,
        percent,
        entryCount: total,
        validEntryCount: total,
        apiStatus: null,
    };
};

const isBatchApplying = (batchNo, applyingBatchNos = []) =>
    applyingBatchNos.some((item) => String(item) === String(batchNo ?? ""));

const buildInProgress = (importedCount, total, percent) => ({
    status: "in-progress",
    labelKey: "business_customers.in_progress",
    className: "processed-ongoing",
    importedCount,
    total,
    percent,
});

const buildSuccessful = (importedCount, total) => ({
    status: "successful",
    labelKey: "business_customers.successful",
    className: "processed-success",
    importedCount,
    total,
    percent: 100,
});

const buildCompletedErrors = (importedCount, total, percent) => ({
    status: "completed-errors",
    labelKey: "business_customers.completed-errors",
    className: "processed-errors",
    importedCount,
    total,
    percent,
});

const buildNotProcessed = (importedCount, total, percent) => ({
    status: "not-processed",
    labelKey: "business_customers.not-processed",
    className: "not-tag mb-0",
    importedCount,
    total,
    percent,
});

const hasPartialCompletion = ({ importedCount, total, entryCount, validEntryCount }) => {
    if (validEntryCount < entryCount) {
        return true;
    }
    return total > 0 && importedCount > 0 && importedCount < total;
};

const getProgressFromApiStatus = (counts) => {
    const { apiStatus, importedCount, total, percent, entryCount, validEntryCount } = counts;

    if (apiStatus === "IN_PROGRESS") {
        return buildInProgress(importedCount, total, percent);
    }

    if (apiStatus === "COMPLETED") {
        if (hasPartialCompletion({ importedCount, total, entryCount, validEntryCount })) {
            const displayTotal = entryCount || total;
            return buildCompletedErrors(
                importedCount,
                displayTotal,
                displayTotal ? Math.round((importedCount / displayTotal) * 100) : percent
            );
        }
        return buildSuccessful(importedCount, total);
    }

    if (apiStatus === "NOT_STARTED") {
        return buildNotProcessed(0, total, 0);
    }

    return null;
};

const getProgressFromRows = (batchRows, applyingBatchNos, batchNo) => {
    const { total, importedCount, percent } = getCountsFromRows(batchRows);

    if (isBatchApplying(batchNo, applyingBatchNos)) {
        return buildInProgress(importedCount, total, percent);
    }

    if (total > 0 && importedCount === total) {
        return buildSuccessful(importedCount, total);
    }

    if (importedCount > 0 && importedCount < total) {
        return buildCompletedErrors(importedCount, total, percent);
    }

    return buildNotProcessed(importedCount, total, percent);
};

export const getBatchProgress = ({
    batchMeta = null,
    batchRows = [],
    batchNo,
    applyingBatchNos = [],
}) => {
    const apiStatus = batchMeta ? getApiStatus(batchMeta) : null;
    const isApplying = isBatchApplying(batchNo, applyingBatchNos);
    const shouldShowApplying = isApplying && apiStatus !== "COMPLETED";

    if (shouldShowApplying) {
        const counts = batchMeta
            ? getCountsFromBatchMeta(batchMeta, batchRows)
            : getCountsFromRows(batchRows);
        return buildInProgress(counts.importedCount, counts.total, counts.percent);
    }

    if (batchMeta) {
        const counts = getCountsFromBatchMeta(batchMeta, batchRows);
        const apiProgress = getProgressFromApiStatus(counts);
        if (apiProgress) {
            return apiProgress;
        }
    }

    return getProgressFromRows(batchRows, applyingBatchNos, batchNo);
};

const resolveBatchStatuses = (batches, applyingBatchNos = []) =>
    normalizeAudienceBatches(batches).map((batch) => {
        const batchNo = String(batch.batchNo ?? "");
        if (isBatchApplying(batchNo, applyingBatchNos)) {
            return "IN_PROGRESS";
        }
        return getApiStatus(batch);
    });

export const hasAnyCompletedBatch = (batches) =>
    resolveBatchStatuses(batches).some((status) => status === "COMPLETED");

export const getAudienceModifiedFromBatches = (batches) => {
    const completedOnDates = normalizeAudienceBatches(batches)
        .map((batch) => batch.completedOn)
        .filter(Boolean);

    if (completedOnDates.length === 0) {
        return { hasBeenModified: false, modifiedDate: "" };
    }

    const latestCompletedOn = [...completedOnDates].sort(
        (a, b) => new Date(b).getTime() - new Date(a).getTime()
    )[0];

    return { hasBeenModified: true, modifiedDate: latestCompletedOn };
};

export const getAudienceStatusFromBatches = (batches, applyingBatchNos = []) => {
    const normalized = normalizeAudienceBatches(batches);
    if (normalized.length === 0) {
        return null;
    }

    const statuses = resolveBatchStatuses(normalized, applyingBatchNos);
    const hasInProgress = statuses.some((status) => status === "IN_PROGRESS");
    const hasNotStarted = statuses.some((status) => status === "NOT_STARTED");
    const hasCompleted = statuses.some((status) => status === "COMPLETED");
    const allNotStarted = statuses.every((status) => status === "NOT_STARTED");
    const allCompleted = statuses.every((status) => status === "COMPLETED");

    if (allNotStarted) {
        return { labelKey: "business_customers.not-processed", className: "not-tag" };
    }

    if (hasInProgress || (hasCompleted && hasNotStarted)) {
        if (hasCompleted && hasNotStarted && !hasInProgress) {
            return { labelKey: "business_customers.partial_apply_status", className: "processed-ongoing" };
        }
        return { labelKey: "business_customers.in_progress", className: "processed-ongoing" };
    }

    if (allCompleted) {
        const hasErrors = normalized.some((batch) =>
            hasPartialCompletion(getCountsFromBatchMeta(batch))
        );
        if (hasErrors) {
            return { labelKey: "business_customers.completed-errors", className: "processed-errors" };
        }
        return { labelKey: "business_customers.successful", className: "processed-success" };
    }

    return { labelKey: "business_customers.in_progress", className: "processed-ongoing" };
};

export const resolveUploadBatchNo = (uploadResponse, batches = null) => {
    if (!uploadResponse) {
        return null;
    }

    const directBatchNo = uploadResponse.batchNo
        ?? uploadResponse.lastBatchNo
        ?? uploadResponse.latestBatchNo;

    if (directBatchNo != null && directBatchNo !== "") {
        return String(directBatchNo);
    }

    const normalized = normalizeAudienceBatches(batches);
    if (normalized.length === 0) {
        return null;
    }

    const notStarted = normalized.filter((batch) => getApiStatus(batch) === "NOT_STARTED");
    const candidates = notStarted.length > 0 ? notStarted : normalized;
    const latest = candidates[candidates.length - 1];
    const batchNo = latest?.batchNo;
    return batchNo != null && batchNo !== "" ? String(batchNo) : null;
};

export const isBatchCompleted = (batches, batchNo) => {
    if (batchNo == null || batchNo === "") {
        return false;
    }
    const batchMeta = buildBatchMetaByBatchNo(batches).get(String(batchNo));
    return batchMeta ? getApiStatus(batchMeta) === "COMPLETED" : false;
};
